import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { ASSETS, IMG } from "@/data/site";
import { EASE } from "@/components/Reveal";
import WhatsAppCTA from "@/components/WhatsAppCTA";

function MaskedLine({ children, delay, className }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        initial={{ y: "115%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.1, delay, ease: EASE }}
        className={`block ${className}`}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0.2]);

  return (
    <section ref={ref} data-testid="hero-section" className="noise-overlay relative flex min-h-[100svh] items-end overflow-hidden bg-obsidian">
      <motion.img
        src={IMG.hero}
        alt="Luxury carved stone interior by ShilpStones Creations"
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 h-full w-full object-cover blur-[2.5px]"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/45 to-obsidian/35" />
      <div className="absolute inset-0 bg-gradient-to-r from-obsidian/80 via-obsidian/30 to-transparent" />

      <motion.div style={{ opacity: fade }} className="relative mx-auto w-full max-w-7xl px-6 pb-28 pt-40 sm:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
          className="mb-8 flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:gap-8"
        >
          <img
            src={ASSETS.logo}
            alt="ShilpStones Creations monogram"
            className="h-36 w-36 rounded-full object-cover ring-2 ring-gold/70 sm:h-48 sm:w-48 lg:h-56 lg:w-56"
          />
          <div>
            <img
              src="/brand-wordmark-name.png"
              alt="ShilpStones Creations"
              data-testid="hero-wordmark"
              style={{ filter: "brightness(1.55) saturate(0.05) contrast(1.1) drop-shadow(0 0 1px rgba(0,0,0,0.85)) drop-shadow(0 2px 12px rgba(0,0,0,0.85)) drop-shadow(0 0 26px rgba(205,212,222,0.45))" }}
              className="w-full max-w-md sm:max-w-2xl lg:max-w-3xl"
            />
            <img
              src="/brand-tagline.png"
              alt="Where Stone Becomes Art"
              data-testid="hero-tagline"
              style={{ filter: "brightness(0) invert(1) drop-shadow(0 1px 6px rgba(0,0,0,0.7))" }}
              className="mt-1 w-full max-w-xs opacity-95 sm:max-w-md lg:max-w-xl"
            />
          </div>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: EASE }}
          className="mt-2 max-w-2xl text-sm leading-relaxed text-stone-300 sm:text-base"
        >
          Natural stone, transformed into art, architecture and timeless spaces.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <Link
            to="/projects"
            data-testid="hero-explore-button"
            className="inline-flex items-center rounded-full bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.18em] text-obsidian transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_0_30px_rgba(212,175,55,0.45)]"
          >
            Explore Our Work
          </Link>
          <WhatsAppCTA
            label="Discuss Your Project"
            context="Hero — Discuss Your Project"
            variant="outline"
            testId="hero-whatsapp-button"
          />
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-8 hidden flex-col items-center gap-2 text-stone-400 sm:flex"
      >
        <span className="text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Scroll</span>
        <ChevronDown className="h-4 w-4 animate-bounce text-gold" />
      </motion.div>
    </section>
  );
}
